import { memo, useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useFormik } from 'formik';
import { useTranslation, type TFunction } from 'react-i18next';
import { isNil, noop, isEmpty, first } from 'lodash';
// Import Types
import type { MenuItemDropdownRef } from './types';
// Import UI Components
import { SkeletonLoader } from '@geneui/components';
// Import Components
import OptionDropdown from 'components/common/OptionDropdown';
// Import Hooks
import { useMenuItem } from 'hooks/UseMenuItem';
// Import Constants
import { l } from 'constants/common';
// Import Config
import { getColumns, getDefaultTranslatableErrorText, getListData, getValues, mapToValue } from './config';
// Import HOC
import { MenuItemContentRefsHOC } from './context';

interface MenuItemDropdownProps {
    menuItemId: number;
    getUpdate: (value: { value?: string[]; isValid?: boolean }) => void;
    onIsLoadingChange?: (isLoading: boolean) => void;
    displayName?: string;
    defaultValue?: { value: string | string[] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    validationSchema?: any;
    isMultiselect?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    supportedValues?: any[];
    placeholder?: string;
    disabled?: boolean;
    isValid?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errorText?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getTranslatableErrorText?: (t: TFunction, error: any) => string;
    className?: string;
    popoverAlign?: string;
    extendTargetWidth?: boolean;
    popoverClassName?: string;
    buttonClassName?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDropdownData?: (data: any[]) => void;
    additionalParams?: Record<string, unknown>;
    doInitialRequest?: boolean;
    depthLevel?: number;
    index?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dropdownExternalOptions?: any[];
    shouldReturnArrayWhenEmpty?: boolean;
}

const MenuItemDropdown = forwardRef<MenuItemDropdownRef, MenuItemDropdownProps>((props, ref) => {
    const {
        menuItemId,
        getUpdate,
        onIsLoadingChange = noop,
        defaultValue,
        isMultiselect = false,
        displayName = '',
        supportedValues = null,
        placeholder = '',
        disabled = false,
        validationSchema = null,
        isValid = true,
        errorText = '',
        getTranslatableErrorText = getDefaultTranslatableErrorText,
        className = '',
        popoverAlign = 'center',
        extendTargetWidth = false,
        popoverClassName = '',
        buttonClassName = '',
        getDropdownData,
        additionalParams = {},
        doInitialRequest = false,
        depthLevel = 1,
        index = 0,
        dropdownExternalOptions,
        shouldReturnArrayWhenEmpty = false,
    } = props;
    const { t } = useTranslation();

    const [showSelected, setShowSelected] = useState(false);

    const [optionLabel, setOptionLabel] = useState('');

    const [nestedMenuItemsData, setNestedMenuItemsData] = useState({});

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [listData, setListData] = useState<any[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [listColumns, setListColumns] = useState<any[]>([]);

    const [searchValue, setSearchValue] = useState({});

    const [value, setValue] = useState(mapToValue(defaultValue?.value));

    const { data, searchData, isLoading, search, config, isLoadingConfig, isLoadingRestore, restore } = useMenuItem(
        menuItemId,
        doInitialRequest,
        null,
        additionalParams,
    );

    const handleClear = useCallback(() => {
        setValue([]);
    }, []);

    useImperativeHandle(ref, () => ({
        updateValue(value) {
            setValue(mapToValue(value));
        },
        reset() {
            handleClear();
        },
        search,
    }));

    const formik = useFormik({
        initialValues: {
            value: getValues(defaultValue?.value, isMultiselect),
        },
        onSubmit: noop,
        validationSchema: validationSchema,
    });
    const { errors, setFieldValue, submitForm } = formik;

    useEffect(() => {
        if (!isNil(config?.option?.keyGroupNameColumn?.name)) {
            if (value.length > 1) {
                setOptionLabel(t(l.OptionsSelected, { count: value.length }));
            } else {
                setOptionLabel(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    [...(dropdownExternalOptions || []), ...(data as any[])]
                        .filter((item) => value.includes(item[config.keyColumnName].toString()))
                        .map((item) => item[config.option.keyGroupNameColumn.name])
                        .join(', '),
                );
            }
        }
        setFieldValue('value', isMultiselect ? value : isEmpty(value) ? null : first(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, data, t, dropdownExternalOptions]);

    useEffect(() => {
        getDropdownData?.(isEmpty(data) ? [] : [...(dropdownExternalOptions || []), ...data]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, dropdownExternalOptions]);

    useEffect(() => {
        const arrayValue = isMultiselect ? value : isEmpty(value) ? null : first(value);
        const nullWhenEmptyValue = isEmpty(value) ? null : isMultiselect ? value : first(value);

        getUpdate({ value: shouldReturnArrayWhenEmpty ? arrayValue : nullWhenEmptyValue });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        onIsLoadingChange(isLoading);
    }, [isLoading, onIsLoadingChange]);

    const handleSearch = useCallback(
        (e) => {
            setSearchValue(e);
            search(e);
        },
        [search],
    );

    const handleShowSelectedChange = (e) => setShowSelected(e);

    useEffect(() => {
        getUpdate({ isValid: !errors.value });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors]);

    useEffect(() => {
        submitForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isLoadingConfig && !isEmpty(config) && !isEmpty(value) && config.isAutocomplete) {
            const restoreIdsWithoutExternalOptions = value.filter(
                (id) => !dropdownExternalOptions?.map(({ Id }) => Id).includes(id),
            );
            restore(restoreIdsWithoutExternalOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingConfig, config]);

    useEffect(() => {
        const newListData = getListData({
            data,
            searchValue,
            searchData,
            config,
            columns: listColumns,
            showSelected,
            supportedValues,
            value,
            nestedMenuItemsData,
            dropdownExternalOptions,
        });
        setListData(newListData);
    }, [
        showSelected,
        data,
        searchValue,
        searchData,
        value,
        config,
        listColumns,
        supportedValues,
        nestedMenuItemsData,
        dropdownExternalOptions,
    ]);

    useEffect(() => {
        const newListColumns = getColumns({ config, displayName, t, setNestedMenuItemsData });
        setListColumns(newListColumns);
    }, [config, displayName, t]);

    return (
        <div className={className}>
            <SkeletonLoader isBusy={isLoadingConfig || isLoadingRestore || (isLoading && !config?.isAutocomplete)}>
                {!isLoadingConfig && !isLoadingRestore && !(isLoading && !config?.isAutocomplete) && (
                    <OptionDropdown
                        optionLabel={optionLabel}
                        isValid={!errors.value && isValid}
                        errorText={getTranslatableErrorText?.(t, errors?.value) || errorText}
                        onClear={handleClear}
                        valueKey={config?.keyColumnName}
                        value={value}
                        isLoading={isLoading}
                        isMultiselect={isMultiselect}
                        columns={listColumns}
                        list={listData}
                        rowCount={listData?.length}
                        onChange={setValue}
                        onSearch={handleSearch}
                        showSelected={showSelected}
                        onChangeShowSelected={handleShowSelectedChange}
                        defaultSearchValue={searchValue}
                        placeholder={placeholder}
                        disabled={disabled}
                        popoverAlign={popoverAlign}
                        extendTargetWidth={extendTargetWidth}
                        popoverClassName={popoverClassName}
                        buttonClassName={buttonClassName}
                        depthLevel={depthLevel}
                        index={index}
                    />
                )}
            </SkeletonLoader>
        </div>
    );
});

MenuItemDropdown.displayName = 'MenuItemDropdown';

export default memo(MenuItemContentRefsHOC(memo(MenuItemDropdown)));
